
import { supabase } from '@/integrations/supabase/client';
import { sanitizeFileName } from './fileValidation';
import { StorageManager, type StorageBucket } from './storage-manager';

interface UploadOptions {
  file: File;
  userId: string;
  path?: string;
  bucket?: string;
  contentType?: string;
}

interface OrganizedUploadOptions {
  file: File;
  userId: string;
  bucket: StorageBucket;
  type?: 'avatar' | 'banner' | 'bulletin' | 'relic' | 'video' | 'thumbnail' | 'drop';
  contentType?: string;
  maxSize?: number;
  collectionId?: string;
  rarity?: 'common' | 'uncommon' | 'rare' | 'legendary';
}

const getMimeTypeFromExtension = (filename: string): string => {
  // Handle filenames with multiple dots (e.g.: "image.1.2.jpg")
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return 'application/octet-stream';
  
  const extension = filename.slice(lastDotIndex + 1).toLowerCase();
  
  const typeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    heic: 'image/heic',
    mp4: 'video/mp4'
  };

  return typeMap[extension] || 'application/octet-stream';
};

export const uploadFile = async ({ 
  file, 
  userId, 
  path,
  bucket = 'bulletin-images',
  contentType
}: UploadOptions) => {
  // Validate file object
  if (!(file instanceof File)) {
    console.error('Invalid file object:', file);
    throw new Error('Invalid file format');
  }

  if (file.size === 0) {
    console.error('Empty file uploaded');
    throw new Error('File is empty');
  }

  const fileName = sanitizeFileName(file.name);
  const timestamp = Date.now();
  const filePath = path || `${userId}/${timestamp}-${fileName}`;

  // Debug logging for MIME type detection
  console.log('Original file details:', {
    name: file.name,
    type: file.type,
    size: file.size
  });

  // Determine content type with multiple fallbacks
  let determinedContentType = contentType || file.type;
  
  // Handle cases where browser reports incorrect type
  if (!determinedContentType || determinedContentType === 'application/json') {
    determinedContentType = getMimeTypeFromExtension(fileName);
  }

  // Final fallback for empty types
  if (!determinedContentType) {
    determinedContentType = 'application/octet-stream';
  }

  console.log('Upload preparation:', {
    fileName,
    originalType: file.type,
    determinedContentType,
    bucket,
    path: filePath
  });

  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      contentType: determinedContentType,
      upsert: true
    });

  if (error) {
    console.error('Upload error:', {
      error,
      fileName,
      contentType: determinedContentType
    });
    throw error;
  }

  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  console.log('Upload successful:', {
    path: filePath,
    publicUrl,
    contentType: determinedContentType
  });

  return { data, publicUrl };
};

// New organized upload function
export const uploadFileOrganized = async (options: OrganizedUploadOptions & { collectionId?: string; rarity?: string }) => {
  return await StorageManager.uploadWithOrganization(options);
};
